import { useRef, useState } from 'react'
import { api } from '../../../lib/api.js'
import { uploadOwnedFile } from '../ownedAsset.js'
import { CREST_ROUTES } from './concept.js'
import styles from './ConceptBrief.module.css'

/**
 * THE CREST, OBTAINED FROM INSIDE THE BLOCKED FLOW.
 *
 * Brian: *"it is requiring a crest logo that is sourced from hubspot but not all
 * logos are in there especially in high school."*
 *
 * ⚠️ THE REFUSAL IS NOT WEAKENED, AND MUST NOT BE. The locked layer injects the
 * client's REAL OWNED FILE and nothing here may draw, source-from-the-web or
 * substitute a mark (`artwork-engine-rebuild-mandate`, `owned-assets`;
 * INSTRUCTIONS.md §9). A concept with no owned crest still BLOCKS. What changes
 * is that the blocker now carries the action instead of only the diagnosis.
 *
 * ⚠️ WHY UPLOAD IS THE PRIMARY CONTROL AND NOT A FALLBACK. Measured live
 * 2026-09-14 through the Studio's own door: **0 of 335 sampled high schools**
 * and **0 of 23 middle schools** carry an owned crest, against 60.6% of
 * colleges — and `/api/hubspot/logo-find` found **nothing for 20 of 20**
 * crest-less high schools. There is no owned-logo property on companies at all;
 * the crest is a File Manager object under `/hubfs/…/SR College Logos/…`, and
 * the folder name tells the story. So for a high school the finder has nothing
 * to find and the Marks Library queue has nothing staged to approve. Pointing a
 * rep at either is pointing them at a dead end, which is why the level read as
 * unusable for the schools that are most of the universe.
 *
 * ⚠️ IT WILL NOT WRITE A `manual reference`. `LogoCPicker` (step 1, level 1)
 * falls back to a typed reference when an upload fails, and that is right there
 * — an operator chose it, in a flow of thirteen deliberate choices. Here nobody
 * chose it, and `conceptBlockers` tests `logoC.name` (mirroring `cleanRef()`),
 * so a reference would SATISFY the blocker while the build still had no owned
 * file. A failed upload therefore reports the failure and writes nothing; the
 * build stays blocked, which is the honest state.
 */
export default function ConceptCrest({ schoolName, onCrest }) {
  const [busy, setBusy] = useState('')
  const [msg, setMsg] = useState('')
  const [tone, setTone] = useState('note')
  const fileRef = useRef(null)

  const say = (t, text) => { setTone(t); setMsg(text) }

  async function upload(file) {
    if (!file) return
    setBusy('upload')
    say('note', '')
    try {
      /* A 200 with no URL is not a crest, and `uploadOwnedFile` throws on one
       * rather than returning a ref with no file behind it — which would pass
       * the blocker while the build still had no owned mark. */
      const owned = await uploadOwnedFile(file, { kind: 'crest' })
      onCrest({
        id: owned.fileId,
        name: schoolName ? `${schoolName} crest` : file.name,
        source: 'HubSpot-owned',
        url: owned.url,
      })
      say('ok', 'Attached as an owned file in HubSpot File Manager. The locked layer injects this exact file — no model redraws it.')
    } catch (err) {
      say('warn', `${err?.message || `The upload failed (${err?.status || 'network error'}).`} Nothing was attached and the build is still blocked. That is deliberate: a crest that is not an owned file is not a crest.`)
    } finally {
      setBusy('')
    }
  }

  async function find() {
    const name = String(schoolName || '').trim()
    if (!name) return
    setBusy('find')
    say('note', '')
    try {
      const res = await api.logoFind({ name })
      if (res?.found && res?.logo) {
        onCrest({
          name: `${res.matched || name} crest`,
          source: res.owned ? 'HubSpot-owned' : 'sourced — owned copy staged for approval',
          url: res.logo,
        })
        say('ok', res.owned
          ? 'Matched a mark already owned on file.'
          : 'Sourced and staged an owned copy — it goes to the Marks Library queue for approval.')
      } else {
        say('warn', `No owned mark on file for “${name}”. Measured across the high-school universe this is the usual answer — upload the crest above.`)
      }
    } catch (err) {
      say('warn', `The mark finder is unavailable (${err?.status || 'network error'}). Upload the crest above.`)
    } finally {
      setBusy('')
    }
  }

  return (
    <div className={styles.crestBox}>
      <div className="field-label">Get the crest — without leaving this screen</div>

      <div className={styles.crestActions}>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!!busy}
          onClick={() => fileRef.current?.click()}
        >
          {busy === 'upload' ? 'Uploading…' : 'Upload the crest from this device'}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,.svg,.pdf"
          hidden
          onChange={(e) => { upload(e.target.files?.[0]); e.target.value = '' }}
        />
        <button
          type="button"
          className="btn"
          disabled={!!busy || !String(schoolName || '').trim()}
          onClick={find}
        >
          {busy === 'find' ? 'Searching…' : 'Search for an owned mark'}
        </button>
      </div>

      {/* Every route is NAMED with what it does and what it is measured to do,
          so a rep picks the one that will work rather than the first one listed.
          The queue that cannot help a school with no mark says so, rather than
          being quietly dropped — it IS the route once something is staged. */}
      <ul className={styles.routes}>
        {CREST_ROUTES.map((r) => (
          <li key={r.id} className={r.works ? styles.route : `${styles.route} ${styles.routeWeak}`}>
            <strong>{r.label}</strong>
            <span>{r.what}</span>
            <span className={styles.routeMeasured}>{r.measured}</span>
          </li>
        ))}
      </ul>

      {msg && <p className={tone === 'ok' ? styles.okNote : tone === 'warn' ? styles.warnNote : styles.note}>{msg}</p>}
    </div>
  )
}
